// =============================================================================
// Module Project - Store: useSpacesStore
// Gestion des espaces (dossiers) pour la sidebar
// =============================================================================

import { create } from 'zustand';
import {
  fetchSpaces,
  createSpace,
  updateSpace,
  deleteSpace,
  assignProjectToSpace,
  type Space,
  type SpaceCreateInput,
  type SpaceUpdateInput,
} from '../data/spaceRepository';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────
export interface SpacesState {
  spaces: Space[];
  loading: boolean;
  error: string | null;
  expandedSpaces: Set<string>;
  
  // Actions
  loadSpaces: (orgId: string) => Promise<void>;
  addSpace: (input: SpaceCreateInput) => Promise<Space>;
  editSpace: (spaceId: string, updates: SpaceUpdateInput) => Promise<void>;
  removeSpace: (spaceId: string) => Promise<void>;
  moveProjectToSpace: (projectId: string, spaceId: string | null) => Promise<void>;
  toggleSpaceExpanded: (spaceId: string) => void;
  reset: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// STORE
// ─────────────────────────────────────────────────────────────────────────────
export const useSpacesStore = create<SpacesState>()((set, get) => ({
  spaces: [],
  loading: false,
  error: null,
  expandedSpaces: new Set<string>(),

  // ───────────────────────────────────────────────────────────────────────────
  // LOAD SPACES
  // ───────────────────────────────────────────────────────────────────────────
  loadSpaces: async (orgId: string) => {
    set({ loading: true, error: null });

    try {
      const spaces = await fetchSpaces(orgId);
      // Auto-expand all spaces by default
      const expandedSpaces = new Set(spaces.map(s => s.id));
      set({ spaces, loading: false, expandedSpaces });
    } catch (err) {
      console.error('Error loading spaces:', err);
      set({
        loading: false,
        error: 'Erreur lors du chargement des espaces',
      });
    }
  },

  // ───────────────────────────────────────────────────────────────────────────
  // ADD SPACE
  // ───────────────────────────────────────────────────────────────────────────
  addSpace: async (input: SpaceCreateInput) => {
    const space = await createSpace(input);
    set(state => {
      const newSpaces = [...state.spaces, space];
      // Sort: Archives always last
      newSpaces.sort((a, b) => {
        if (a.is_archive && !b.is_archive) return 1;
        if (!a.is_archive && b.is_archive) return -1;
        return (a.position ?? 0) - (b.position ?? 0);
      });
      return {
        spaces: newSpaces,
        expandedSpaces: new Set([...state.expandedSpaces, space.id]),
      };
    });
    return space;
  },

  // ───────────────────────────────────────────────────────────────────────────
  // EDIT SPACE
  // ───────────────────────────────────────────────────────────────────────────
  editSpace: async (spaceId: string, updates: SpaceUpdateInput) => {
    const updatedSpace = await updateSpace(spaceId, updates);
    set(state => ({
      spaces: state.spaces.map(s => (s.id === spaceId ? updatedSpace : s)),
    }));
  },

  // ───────────────────────────────────────────────────────────────────────────
  // REMOVE SPACE
  // ───────────────────────────────────────────────────────────────────────────
  removeSpace: async (spaceId: string) => {
    await deleteSpace(spaceId);
    set(state => ({
      spaces: state.spaces.filter(s => s.id !== spaceId),
    }));
  },

  // ───────────────────────────────────────────────────────────────────────────
  // MOVE PROJECT TO SPACE
  // ───────────────────────────────────────────────────────────────────────────
  moveProjectToSpace: async (projectId: string, spaceId: string | null) => {
    await assignProjectToSpace(projectId, spaceId);
    // Note: Projects store should be refreshed externally
  },

  // ───────────────────────────────────────────────────────────────────────────
  // TOGGLE EXPAND
  // ───────────────────────────────────────────────────────────────────────────
  toggleSpaceExpanded: (spaceId: string) => {
    set(state => {
      const newExpanded = new Set(state.expandedSpaces);
      if (newExpanded.has(spaceId)) {
        newExpanded.delete(spaceId);
      } else {
        newExpanded.add(spaceId);
      }
      return { expandedSpaces: newExpanded };
    });
  },

  // ───────────────────────────────────────────────────────────────────────────
  // RESET
  // ───────────────────────────────────────────────────────────────────────────
  reset: () => {
    set({
      spaces: [],
      loading: false,
      error: null,
      expandedSpaces: new Set(),
    });
  },
}));
